import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { MdTrendingUp, MdPeople, MdEventNote, MdCalendarToday } from 'react-icons/md';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { dashboardAPI } from '../services/api';

const DashboardContainer = styled.div`
  max-width: 1400px;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const Title = styled.h1`
  font-size: ${props => props.theme.fontSizes.xxxl};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
`;

const Button = styled.button`
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  background-color: ${props => props.primary ? props.theme.colors.primary : props.theme.colors.backgroundLight};
  color: ${props => props.primary ? '#fff' : props.theme.colors.textPrimary};
  border: 1px solid ${props => props.primary ? props.theme.colors.primary : props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  transition: ${props => props.theme.transitions.fast};

  &:hover {
    background-color: ${props => props.primary ? props.theme.colors.primaryDark : props.theme.colors.background};
    transform: translateY(-1px);
    box-shadow: ${props => props.theme.shadows.sm};
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const StatCard = styled.div`
  background: ${props => props.theme.colors.backgroundLight};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.lg};
  box-shadow: ${props => props.theme.shadows.sm};
  border: 1px solid ${props => props.theme.colors.border};
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const StatIcon = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.color || props.theme.colors.primary}15;
  color: ${props => props.color || props.theme.colors.primary};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 1.5rem;
`;

const StatLabel = styled.div`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  font-weight: ${props => props.theme.fontWeights.medium};
`;

const StatValue = styled.div`
  font-size: ${props => props.theme.fontSizes.xxxl};
  font-weight: ${props => props.theme.fontWeights.bold};
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const StatChange = styled.div`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.positive ? props.theme.colors.success : props.theme.colors.danger};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
`;

const ChartSection = styled.div`
  background: ${props => props.theme.colors.backgroundLight};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  box-shadow: ${props => props.theme.shadows.sm};
  border: 1px solid ${props => props.theme.colors.border};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const SectionTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes.xl};
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const ClientsSection = styled.div`
  background: ${props => props.theme.colors.backgroundLight};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  box-shadow: ${props => props.theme.shadows.sm};
  border: 1px solid ${props => props.theme.colors.border};
`;

const ClientsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const ViewAllLink = styled.button`
  color: ${props => props.theme.colors.primary};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const ClientNameLink = styled.button`
  color: ${props => props.theme.colors.textPrimary};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  text-align: left;
  transition: ${props => props.theme.transitions.fast};

  &:hover {
    color: ${props => props.theme.colors.primary};
    text-decoration: underline;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background-color: ${props => props.theme.colors.background};
`;

const Th = styled.th`
  padding: ${props => props.theme.spacing.md};
  text-align: left;
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.semibold};
  color: ${props => props.theme.colors.textSecondary};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const Td = styled.td`
  padding: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textPrimary};
  border-bottom: 1px solid ${props => props.theme.colors.borderLight};
`;

const StatusBadge = styled.span`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: ${props => props.theme.fontWeights.medium};
  background-color: ${props => {
    if (props.status === 'Al Corriente') return props.theme.colors.success + '20';
    if (props.status === 'Vencido') return props.theme.colors.danger + '20';
    return props.theme.colors.warning + '20';
  }};
  color: ${props => {
    if (props.status === 'Al Corriente') return props.theme.colors.success;
    if (props.status === 'Vencido') return props.theme.colors.danger;
    return props.theme.colors.warning;
  }};
`;

const ActionButton = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.primary};
  color: white;
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  cursor: pointer;
  transition: ${props => props.theme.transitions.fast};

  &:hover {
    background-color: ${props => props.theme.colors.primaryDark};
    transform: translateY(-1px);
  }
`;

const Dashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const response = await dashboardAPI.getStats();
        setDashboardData(response.data.data);
        setError(null);
      } catch (err) {
        setError(err.message || 'Error al cargar datos del dashboard');
        console.error('Error al cargar dashboard:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <DashboardContainer>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Cargando dashboard...</p>
        </div>
      </DashboardContainer>
    );
  }

  if (error) {
    return (
      <DashboardContainer>
        <div style={{ textAlign: 'center', padding: '40px', color: '#E57373' }}>
          <p>Error: {error}</p>
          <Button onClick={() => window.location.reload()}>Reintentar</Button>
        </div>
      </DashboardContainer>
    );
  }

  if (!dashboardData) {
    return null;
  }

  const stats = dashboardData.stats || {};
  const monthlyTrends = dashboardData.monthlyTrends || [];
  const recentClients = dashboardData.recentClients || [];

  return (
    <DashboardContainer>
      <PageHeader>
        <Title>Dashboard General</Title>
        <ActionButtons>
          <Button primary onClick={() => navigate('/registrar-pago')}>
            <MdEventNote />
            Registrar Pago
          </Button>
          <Button onClick={() => navigate('/clientes/nuevo')}>
            <MdPeople />
            Nuevo Cliente
          </Button>
        </ActionButtons>
      </PageHeader>

      <StatsGrid>
        <StatCard>
          <StatHeader>
            <StatIcon color="#5B9BD5">
              <MdTrendingUp />
            </StatIcon>
            <StatLabel>Ingresos Totales</StatLabel>
          </StatHeader>
          <StatValue>${(stats.totalIncome || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</StatValue>
          <StatChange positive={stats.incomeChange > 0}>
            {stats.incomeChange > 0 ? '+' : ''}{stats.incomeChange || 0}% desde el mes pasado
          </StatChange>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon color="#5B9BD5">
              <MdPeople />
            </StatIcon>
            <StatLabel>Clientes Activos</StatLabel>
          </StatHeader>
          <StatValue>{stats.activeClients || 0}</StatValue>
          <StatChange positive>
            +{stats.newClients || 0} nuevos este mes
          </StatChange>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon color="#E57373">
              <MdEventNote />
            </StatIcon>
            <StatLabel>Pagos Vencidos</StatLabel>
          </StatHeader>
          <StatValue>${(stats.overduePayments || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</StatValue>
          <StatChange>
            {stats.overdueCount || 0} recibos con pagos atrasados
          </StatChange>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon color="#FFA726">
              <MdCalendarToday />
            </StatIcon>
            <StatLabel>Próximos Vencimientos</StatLabel>
          </StatHeader>
          <StatValue>${(stats.upcomingPayments || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</StatValue>
          <StatChange>
            {stats.upcomingCount || 0} pagos en los próximos 7 días
          </StatChange>
        </StatCard>
      </StatsGrid>

      <ChartSection>
        <SectionTitle>Tendencias de Pagos Mensuales</SectionTitle>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={monthlyTrends}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="mes" stroke="#6C757D" />
            <YAxis stroke="#6C757D" />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#fff', 
                border: '1px solid #e0e0e0',
                borderRadius: '8px'
              }}
            />
            <Legend />
            <Bar dataKey="total_ingresos" fill="#5B9BD5" name="Ingresos" radius={[8, 8, 0, 0]} />
            <Bar dataKey="total_pagos" fill="#E57373" name="Pagos" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartSection>

      <ClientsSection>
        <ClientsHeader>
          <SectionTitle style={{ margin: 0 }}>Clientes Recientes</SectionTitle>
          <ViewAllLink onClick={() => navigate('/clientes')}>Ver todos los clientes</ViewAllLink>
        </ClientsHeader>
        
        <Table>
          <Thead>
            <tr>
              <Th>Cliente</Th>
              <Th>Último Pago</Th>
              <Th>Fecha</Th>
              <Th>Estado</Th>
              <Th>Acciones</Th>
            </tr>
          </Thead>
          <tbody>
            {recentClients.length > 0 ? (
              recentClients.map(client => (
                <tr key={client.cliente_id}>
                  <Td>
                    <ClientNameLink onClick={() => navigate(`/clientes/${client.cliente_id}`)}>
                      {client.nombre}
                    </ClientNameLink>
                  </Td>
                  <Td>${(client.ultimo_monto || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</Td>
                  <Td>{client.ultima_fecha ? new Date(client.ultima_fecha).toLocaleDateString('es-MX') : 'N/A'}</Td>
                  <Td>
                    <StatusBadge status={client.estado}>{client.estado}</StatusBadge>
                  </Td>
                  <Td>
                    <ActionButton onClick={() => navigate(`/clientes/${client.cliente_id}`)}>
                      Ver Detalles
                    </ActionButton>
                  </Td>
                </tr>
              ))
            ) : (
              <tr>
                <Td colSpan="5" style={{ textAlign: 'center' }}>No hay clientes recientes</Td>
              </tr>
            )}
          </tbody>
        </Table>
      </ClientsSection>
    </DashboardContainer>
  );
};

export default Dashboard;
